<template>
  <div>
    <h1 class="heading-1 mb-24">{{ i18n(translations.title) }}</h1>
    <p class="body-1 mb-24">{{ i18n(translations.description) }}</p>

    <RevLoadingScreen
      v-if="isSaving || isLoading"
      :text="
        i18n(
          isSaving
            ? translations.savingBeneficialOwnerDetails
            : translations.loadingBeneficialOwnerDetails,
        )
      "
    />
    <RevForm
      v-else
      :id="FORM_ID"
      :initial-values="initialFormValues"
      :name="FORM_ID"
      :validate="validate()"
      @submit="handleSaveAll"
    >
      <template #default="{ values, hasError }">
        <div class="!border-static-default-mid !rounded-sm border-1">
          <RevAccordionItem
            :collapsed="primaryAccordionState"
            @click.stop.prevent="handlePrimaryAccordionClick"
          >
            <template #title>
              <span class="body-1-bold">
                {{ i18n(translations.primaryBeneficialOwner) }}
              </span>
            </template>
            <template #body>
              <RevCheckbox
                id="same_as_account_requester"
                class="mb-32"
                :disabled="isAccountRequesterCheckboxDisabled"
                :model-value="isSameAsAccountRequester"
                @update:model-value="
                  (val) => handleCheckSameAsAccountRequester(!!val)
                "
              >
                <template #label>
                  {{ i18n(translations.sameAsAccountRequesterLabel) }}
                </template>
              </RevCheckbox>
              <RevCheckbox
                id="same_as_legal_representative"
                class="mb-32"
                :disabled="isLegalRepresentativeCheckboxDisabled"
                :model-value="isSameAsLegalRepresentative"
                @update:model-value="
                  (val) => handleCheckSameAsLegalRepresentative(!!val)
                "
              >
                <template #label>
                  {{ i18n(translations.sameAsLegalRepresentativeLabel) }}
                </template>
              </RevCheckbox>
              <StakeholderForm
                v-model:address-line1="values.primary.addressLine1"
                v-model:address-line2="values.primary.addressLine2"
                v-model:city="values.primary.city"
                v-model:country="values.primary.country"
                v-model:country-of-birth="values.primary.countryOfBirth"
                v-model:country-of-citizenship="
                  values.primary.countryOfCitizenship
                "
                v-model:country-of-issue="values.primary.countryOfIssue"
                v-model:country-of-residency="values.primary.countryOfResidency"
                v-model:date-of-birth="values.primary.dateOfBirth"
                v-model:first-name="values.primary.firstName"
                v-model:identification-type="values.primary.identificationType"
                v-model:kana-first-name="values.primary.kanaFirstName"
                v-model:kana-last-name="values.primary.kanaLastName"
                v-model:last-name="values.primary.lastName"
                v-model:latin-first-name="values.primary.latinFirstName"
                v-model:latin-last-name="values.primary.latinLastName"
                v-model:phone-number="values.primary.phoneNumber"
                v-model:photo-id-back="values.primary.photoIdBack"
                v-model:photo-id-front="values.primary.photoIdFront"
                v-model:postal-code="values.primary.postalCode"
                :disabled="isFormDisabled"
                :errors="{}"
                :feature-code="FeatureCode.WEB_KYB_BENEFICIAL_OWNER"
              />
            </template>
          </RevAccordionItem>
        </div>

        <div v-if="values.secondaries.length > 0" class="mt-24">
          <div
            v-for="(secondary, index) in values.secondaries"
            :key="secondary.id || index"
            class="!border-static-default-mid !rounded-sm mb-16 border-1"
          >
            <RevAccordionItem @click.stop.prevent>
              <template #title>
                <span class="body-1-bold">
                  {{ i18n(translations.additionalBeneficialOwner) }}
                  {{ index + 1 }}
                </span>
              </template>
              <template #body>
                <StakeholderForm
                  v-model:address-line1="values.secondaries[index].addressLine1"
                  v-model:address-line2="values.secondaries[index].addressLine2"
                  v-model:city="values.secondaries[index].city"
                  v-model:country="values.secondaries[index].country"
                  v-model:country-of-birth="
                    values.secondaries[index].countryOfBirth
                  "
                  v-model:country-of-citizenship="
                    values.secondaries[index].countryOfCitizenship
                  "
                  v-model:country-of-issue="
                    values.secondaries[index].countryOfIssue
                  "
                  v-model:country-of-residency="
                    values.secondaries[index].countryOfResidency
                  "
                  v-model:date-of-birth="values.secondaries[index].dateOfBirth"
                  v-model:first-name="values.secondaries[index].firstName"
                  v-model:identification-type="
                    values.secondaries[index].identificationType
                  "
                  v-model:kana-first-name="
                    values.secondaries[index].kanaFirstName
                  "
                  v-model:kana-last-name="
                    values.secondaries[index].kanaLastName
                  "
                  v-model:last-name="values.secondaries[index].lastName"
                  v-model:latin-first-name="
                    values.secondaries[index].latinFirstName
                  "
                  v-model:latin-last-name="
                    values.secondaries[index].latinLastName
                  "
                  v-model:phone-number="values.secondaries[index].phoneNumber"
                  v-model:photo-id-back="values.secondaries[index].photoIdBack"
                  v-model:photo-id-front="
                    values.secondaries[index].photoIdFront
                  "
                  v-model:postal-code="values.secondaries[index].postalCode"
                  :disabled="false"
                  :errors="{}"
                  :feature-code="FeatureCode.WEB_KYB_BENEFICIAL_OWNER"
                />
                <div class="mt-24 flex justify-center">
                  <RevButton
                    class="!border-static-danger-hi !text-static-danger-hi"
                    variant="secondary"
                    @click="removeSecondaryBeneficialOwner(index, values)"
                  >
                    {{ i18n(translations.removeBeneficialOwner) }}
                  </RevButton>
                </div>
              </template>
            </RevAccordionItem>
          </div>
        </div>

        <div class="mt-24 flex justify-center">
          <RevButton
            class="w-full"
            variant="secondary"
            @click="addSecondaryBeneficialOwner(values)"
          >
            {{ i18n(translations.addBeneficialOwner) }}
          </RevButton>
        </div>

        <div class="mt-48 flex justify-center">
          <RevButton
            class="w-full"
            :disabled="hasError || isSaving"
            :form="FORM_ID"
            :loading="isSaving"
            type="submit"
            variant="primary"
          >
            {{ i18n(translations.saveButton) }}
          </RevButton>
        </div>
      </template>
    </RevForm>
  </div>
</template>

<script setup lang="ts">
import { navigateTo, useHead } from '#imports'
import { computed, ref } from 'vue'

import { HttpApiError } from '@backmarket/http-api'
import { FeatureCode } from '@backmarket/nuxt-module-address/featureCode'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import {
  type CollapsedStates,
  RevAccordionItem,
} from '@ds/components/AccordionItem'
import { RevButton } from '@ds/components/Button'
import { RevCheckbox } from '@ds/components/Checkbox'
import { RevForm, makeValidate } from '@ds/components/Form'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'

import { StakeholderRole } from '~/api/kyb/types'
import { ROUTE_NAMES } from '~/constants/routes'
import { useKybData } from '~/scopes/kyb/composables/useKybData'
import { useKybStakeholder } from '~/scopes/kyb/composables/useKybStakeholder'
import type { StakeholderFormValues } from '~/scopes/kyb/pages/components/steps/components/StakeholderForm/StakeholderForm.types'
import {
  createEmptyStakeholderFormValues,
  createStakeholderFormValues,
} from '~/scopes/kyb/utils/kyb.utils'

import StakeholderForm from '../components/StakeholderForm/StakeholderForm.vue'

import translations from './BeneficialOwner.translations'

type BeneficialOwnerFormValues = {
  primary: StakeholderFormValues
  secondaries: StakeholderFormValues[]
}
const i18n = useI18n()
useHead({ title: i18n(translations.pageTitle) })
const { openSuccessToast, openErrorToast } = useTheToast()
const {
  primaryBeneficialOwner,
  secondaryBeneficialOwners,
  accountRequester,
  legalRepresentative,
  saveStakeholder,
  addRolesToStakeholder,
  deleteStakeholder,
  refreshKybData,
  isLoading,
} = useKybData()
const { fillFormAsSameAs } = useKybStakeholder()

const FORM_ID = 'kyb_beneficial_owners_form'

const isSaving = ref(false)
const isFormDisabled = ref(false)
const isAccountRequesterCheckboxDisabled = ref(false)
const isLegalRepresentativeCheckboxDisabled = ref(false)
const primaryAccordionState = ref<CollapsedStates>('expanded')

const initialFormValues = computed(
  (): BeneficialOwnerFormValues => ({
    primary: createStakeholderFormValues(primaryBeneficialOwner.value),
    secondaries: secondaryBeneficialOwners.value.map(
      createStakeholderFormValues,
    ),
  }),
)

const isSameAsAccountRequester = ref(
  primaryBeneficialOwner.value?.id === accountRequester.value?.id,
)
const isSameAsLegalRepresentative = ref(
  primaryBeneficialOwner.value?.id === legalRepresentative.value?.id,
)

function handlePrimaryAccordionClick() {
  primaryAccordionState.value =
    primaryAccordionState.value === 'expanded' ? 'collapsed' : 'expanded'
}

function handleCheckSameAsAccountRequester(value: boolean) {
  isSameAsAccountRequester.value = value

  if (value) {
    isFormDisabled.value = true
    isLegalRepresentativeCheckboxDisabled.value = true

    fillFormAsSameAs(FORM_ID, accountRequester.value, 'primary')
  } else {
    isFormDisabled.value = false
    isLegalRepresentativeCheckboxDisabled.value = false
    fillFormAsSameAs(FORM_ID, primaryBeneficialOwner.value, 'primary')
  }
}

function handleCheckSameAsLegalRepresentative(value: boolean) {
  isSameAsLegalRepresentative.value = value

  if (value) {
    isFormDisabled.value = true
    isSameAsAccountRequester.value = false

    fillFormAsSameAs(FORM_ID, legalRepresentative.value, 'primary')
  } else {
    isFormDisabled.value = false
    isSameAsAccountRequester.value = false
    fillFormAsSameAs(FORM_ID, primaryBeneficialOwner.value, 'primary')
  }
}

function addSecondaryBeneficialOwner(formValues: BeneficialOwnerFormValues) {
  const newOwner: StakeholderFormValues = {
    ...createEmptyStakeholderFormValues(),
    id: null,
  }
  formValues.secondaries.push(newOwner)
}

function removeSecondaryBeneficialOwner(
  index: number,
  formValues: BeneficialOwnerFormValues,
) {
  formValues.secondaries.splice(index, 1)
}

async function handleSaveAll(formValues: BeneficialOwnerFormValues) {
  isSaving.value = true

  try {
    if (isSameAsAccountRequester.value && accountRequester.value) {
      await addRolesToStakeholder(accountRequester.value, [
        StakeholderRole.PRIMARY_BENEFICIAL_OWNER,
      ])
    } else if (isSameAsLegalRepresentative.value && legalRepresentative.value) {
      await addRolesToStakeholder(legalRepresentative.value, [
        StakeholderRole.PRIMARY_BENEFICIAL_OWNER,
      ])
    } else {
      await saveStakeholder(formValues.primary, [
        StakeholderRole.PRIMARY_BENEFICIAL_OWNER,
      ])
    }

    // Get IDs of current secondary owners in the form
    const localSecondaryOwnersIds = new Set(
      formValues.secondaries.map((owner) => owner.id).filter(Boolean),
    )

    // Delete secondary owners that have been removed from the list
    for (const owner of secondaryBeneficialOwners.value) {
      if (owner.id && !localSecondaryOwnersIds.has(owner.id)) {
        // eslint-disable-next-line no-await-in-loop
        await deleteStakeholder(owner.id)
      }
    }

    // Save/update remaining secondary owners
    for (const secondaryOwner of formValues.secondaries) {
      // eslint-disable-next-line no-await-in-loop
      await saveStakeholder(secondaryOwner, [
        StakeholderRole.SECONDARY_BENEFICIAL_OWNER,
      ])
    }

    openSuccessToast({
      title: i18n(translations.saveSuccessTitle),
      content: i18n(translations.saveSuccessContent),
    })

    navigateTo({ name: ROUTE_NAMES.KYB.STEPS.STEP_5 })
    refreshKybData()
  } catch (error) {
    const { title: content } = HttpApiError.fromAnyError(error as Error)

    openErrorToast({
      title: i18n(translations.saveErrorTitle),
      content,
    })
  } finally {
    isSaving.value = false
  }
}

function validate() {
  // TODO: Implement validation for primary and secondary beneficial owners (dynamic)
  return makeValidate<BeneficialOwnerFormValues>({})
}
</script>
